import type { PlayerConfig } from '../config.js';
import type { EventBatchWire } from '../events/types.js';
import type { Logger } from '../log.js';
import { parseManifest } from '../manifest/parse.js';
import type { Manifest } from '../manifest/types.js';
import { DEVICE_AUTH_HEADER, deviceAuthorizationHeader } from './device-auth.js';
import { isDeviceTokenRevokedResponse } from './revocation.js';

/**
 * The device endpoints of technical-design §6.6, behind an interface so the harness
 * can drive the state machine without a network. Every call is safe to repeat:
 * manifest fetch is conditional, event upload is idempotent on the client-generated
 * id, heartbeat is a snapshot (architecture-v2 §9, "idempotency everywhere").
 */

export interface HeartbeatPayload {
  readonly app_version: string;
  readonly cache_bytes: number;
  readonly free_bytes: number | null;
  readonly manifest_ver: number | null;
  readonly uptime_s: number;
  readonly last_error: string | null;
  /**
   * decision-log D174: the network this screen is on, so a stick moved to another venue can be
   * spotted. `null` whenever the shell could not read one. Every key in this interface must be a
   * field on the API's `HeartbeatIn`; `api/tests/routes_device/test_player_heartbeat_matches_this_schema.py`
   * reads this file and fails if one is not.
   */
  readonly wifi_ssid: string | null;
  /**
   * The device's own clock at send time, ISO-8601. The server subtracts it from its own clock
   * and that difference is what `devices.clock_offset_seconds` holds — the column, its
   * migration, the admin surface and the retention entry all shipped, and all sat empty,
   * because nothing ever sent this.
   */
  readonly device_clock_at: string;
  /** design-addendum §11: the device's clock offset, flagged past five minutes. */
  readonly clock_offset_ms: number;
  readonly clock_drift_flagged: boolean;
  readonly queued_events: number;
  /**
   * dwf-063 / decision-log D211: whether the player held the foreground when this was
   * built. `null` is "cannot tell" — no shell to ask — and must never be read as
   * backgrounded. A device behind Amazon's launcher heartbeats exactly like a playing one
   * without this field.
   */
  readonly foreground: boolean | null;
  /**
   * dwf-064 / decision-log D213, the fleet census carried on the heartbeat so it keeps
   * itself current instead of being re-run. All `null` in a browser tab.
   */
  readonly hardware_model: string | null;
  readonly os_version: string | null;
  readonly sdk_int: number | null;
  readonly overlay_op_granted: boolean | null;
  /** `wifi` | `ethernet` | `unknown`; D174's check is meaningless without it on a wired set. */
  readonly network_type: string | null;
  /**
   * dwf-064 / D213: whether the television panel itself is on. `null` is "cannot tell" and
   * must never be read as off; `false` is a panel in standby that is still heartbeating.
   */
  readonly screen_on: boolean | null;
  /** dwf-064 §2: SHA-256 of the shell's signing certificate, lowercase hex, no colons. */
  readonly signing_sha256: string | null;
}

export type ManifestFetch =
  | { readonly status: 'unchanged' }
  | { readonly status: 'updated'; readonly manifest: Manifest; readonly etag: string | null }
  | { readonly status: 'invalid'; readonly errors: readonly string[] }
  /**
   * The token is gone for good: show the "not activated" card (design-addendum §11).
   * Distinguishing this from a transient 4xx is decision-log D81; the player treats
   * a 401/403 carrying `urn:digitalwave:errors:device-token-revoked` as revocation
   * and every other 4xx as "keep the last known-good loop" (technical-design §11.7).
   */
  | { readonly status: 'revoked' }
  | { readonly status: 'client_error'; readonly http_status: number }
  | { readonly status: 'unavailable'; readonly reason: string };

export type UploadResult =
  | { readonly status: 'accepted' }
  /** Heartbeat-only: same problem type as manifest revocation (decision-log D81). */
  | { readonly status: 'revoked' }
  | { readonly status: 'rejected'; readonly http_status: number }
  | { readonly status: 'unavailable'; readonly reason: string };

export interface DeviceApi {
  fetchManifest(etag: string | null): Promise<ManifestFetch>;
  postEvents(batch: EventBatchWire): Promise<UploadResult>;
  postHeartbeat(payload: HeartbeatPayload): Promise<UploadResult>;
}

interface HttpResponseLike {
  readonly status: number;
  readonly headers: { get(name: string): string | null };
  text(): Promise<string>;
}

export type HttpFetch = (
  url: string,
  init: { method: string; headers: Record<string, string>; body?: string },
) => Promise<HttpResponseLike>;

export interface HttpDeviceApiOptions {
  readonly config: PlayerConfig;
  readonly fetch: HttpFetch;
  readonly deviceToken: () => string | null;
  readonly logger: Logger;
  /** Clock discipline: every response's `Date` header corrects the device clock. */
  readonly observeServerTime: (serverMs: number) => void;
}

export function createHttpDeviceApi(options: HttpDeviceApiOptions): DeviceApi {
  const url = (path: string): string => `${options.config.apiBaseUrl}${path}`;

  /**
   * The only place a device request grows credentials. The scheme comes from `device-auth.ts`
   * (decision-log D83) rather than being spelled here: it was spelled here, as `Bearer`, and
   * every request in the fleet 401'd.
   */
  const authHeaders = (): Record<string, string> | null => {
    const token = options.deviceToken();
    if (token === null) return null;
    return { [DEVICE_AUTH_HEADER]: deviceAuthorizationHeader(token) };
  };

  const observeDate = (response: HttpResponseLike): void => {
    const header = response.headers.get('date');
    if (header === null) return;
    const serverMs = Date.parse(header);
    if (!Number.isNaN(serverMs)) options.observeServerTime(serverMs);
  };

  return {
    async fetchManifest(etag) {
      const headers = authHeaders();
      if (headers === null) return { status: 'revoked' };
      if (etag !== null) headers['If-None-Match'] = etag;

      let response: HttpResponseLike;
      try {
        response = await options.fetch(url('/device/manifest'), { method: 'GET', headers });
      } catch (error) {
        return { status: 'unavailable', reason: describe(error) };
      }
      observeDate(response);

      if (response.status === 304) return { status: 'unchanged' };

      if (response.status === 200) {
        let parsedJson: unknown;
        try {
          parsedJson = JSON.parse(await response.text());
        } catch (error) {
          return { status: 'invalid', errors: [describe(error)] };
        }
        const parsed = parseManifest(parsedJson);
        if (!parsed.ok) return { status: 'invalid', errors: parsed.errors };
        if (parsed.skipped.length > 0) {
          // Error level on purpose: until the heartbeat has a field of its own (decision-log
          // O198), `last_error` is the only way a skipped item reaches the cloud.
          options.logger.error('manifest.items_skipped', {
            manifest_version: parsed.value.manifest_version,
            skipped: parsed.skipped.length,
            items: parsed.skipped.slice(0, 5).map((entry) => ({
              index: entry.index,
              item_id: entry.item_id,
              errors: entry.errors.slice(0, 3),
            })),
          });
        }
        if (parsed.defaulted.length > 0) {
          options.logger.warn('manifest.fields_defaulted', {
            manifest_version: parsed.value.manifest_version,
            fields: parsed.defaulted.slice(0, 5),
          });
        }
        return { status: 'updated', manifest: parsed.value, etag: response.headers.get('etag') };
      }

      if (response.status >= 400 && response.status < 500) {
        const body = await response.text();
        if (isDeviceTokenRevokedResponse(response.status, body)) return { status: 'revoked' };
        return { status: 'client_error', http_status: response.status };
      }

      return { status: 'unavailable', reason: `http_${String(response.status)}` };
    },

    postEvents: (batch) => post('/device/events', batch, false),
    postHeartbeat: (payload) => post('/device/heartbeat', payload, true),
  };

  async function post(
    path: string,
    body: unknown,
    checkRevocation: boolean,
  ): Promise<UploadResult> {
    const headers = authHeaders();
    if (headers === null) return { status: 'rejected', http_status: 401 };
    headers['Content-Type'] = 'application/json';

    let response: HttpResponseLike;
    try {
      response = await options.fetch(url(path), {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });
    } catch (error) {
      return { status: 'unavailable', reason: describe(error) };
    }
    observeDate(response);

    if (response.status >= 200 && response.status < 300) return { status: 'accepted' };
    if (response.status >= 400 && response.status < 500) {
      const responseBody = await response.text();
      if (checkRevocation && isDeviceTokenRevokedResponse(response.status, responseBody)) {
        return { status: 'revoked' };
      }
      options.logger.warn('device_api.rejected', { path, status: response.status });
      return { status: 'rejected', http_status: response.status };
    }
    return { status: 'unavailable', reason: `http_${String(response.status)}` };
  }
}

function describe(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
